import * as _ from 'lodash';

import { CalibrationUsesValModel } from '../calibrations/calibration-uses-val.model';
import { TableSettings } from '../common-models/table-settings.model';
import tableSettings from './settings/uses-table.settings';

import ICalibrationUsesVal = ZoneIQCalibration.ICalibrationUsesVal;
import ICalibrationUsesType = ZoneIQCalibration.ICalibrationUsesType;

import { AllowedUseModel } from '..';
import { IUsesTableModel } from 'src/app/interfaces/calibration-set-table.interface';
import { IUsesTableColumn } from 'src/app/interfaces/tables/cells/uses-table-column.interface';
import { ICalibrationSetModel } from 'src/app/interfaces/calibration-set-model.interface';
import { IBaseTableCell } from 'src/app/interfaces/tables/cells/base-table-cell-interface';
import { ICalibrationSetValidationResult } from 'src/app/interfaces/zc-validation-result.interface';
import { UsesTableColumn } from './uses-table-column.model';

export class UsesTableModel implements IUsesTableModel {
    settings: TableSettings;
    usesTypes: ICalibrationUsesType[];

    private table: IUsesTableColumn[] = [];
    private isInitialized = false;
    private isInitializing = false;

    constructor(calibrationUsesTypes) {
        this.usesTypes = calibrationUsesTypes;
        this.settings = new TableSettings(tableSettings);
    }

    get value() {
        return this.table;
    }

    get isChanged() {
        return this.table.some(column => {
            return column.usesValues.some(use => (use.value as CalibrationUsesValModel).isChanged);
        });
    }

    get isValid() {
        return !this.table.some(column => {
            return column.usesValues.some(use => !(use.value as CalibrationUsesValModel).isValid());
        });
    }

    init(calibrationSets: ICalibrationSetModel<any>[]) {
        if (!this.usesTypes || !_.get(calibrationSets, 'length', 0)) {
            return;
        }
        if (this.isInitializing || this.isInitialized) {
            return;
        }
        this.isInitializing = true;

        // usesTypes and zoningCodes are separate objects
        // table is array of object - calibrationSet

        calibrationSets.forEach(calibrationSet => {
            const uses = new Map<string, IBaseTableCell<ICalibrationUsesVal>>();
            const labels = this.usesTypes.reduce((acc, value) => {
                return acc.concat(value.labels);
            }, []);

            labels.forEach(label => {
                const existingUse = calibrationSet.CalibrationUsesVals.find(
                    e => (e as CalibrationUsesValModel).origin.CalibrationUsesLabelId === label.Id
                );
                if (existingUse) {
                    (existingUse as CalibrationUsesValModel).fieldsSettings = this.settings.fieldValidation;
                }

                uses.set(label.Id, {
                    calibrationSet: null,
                    value: existingUse as CalibrationUsesValModel
                });
            });

            this.table.push(new UsesTableColumn(calibrationSet, uses));
        });
        this.table.sort((a, b) =>
            a.calibrationSet.Name.toLowerCase() > b.calibrationSet.Name.toLowerCase()
                ? 1
                : b.calibrationSet.Name.toLowerCase() > a.calibrationSet.Name.toLowerCase()
                ? -1
                : 0
        );
        this.isInitializing = false;
        this.isInitialized = true;
    }

    getChangedData(notAllowedUse: AllowedUseModel): ICalibrationUsesVal[] {
        if (!notAllowedUse) {
            return [];
        }
        const notAllowedUseId = notAllowedUse.Id;
        return this.table.reduce((result, column) => {
            const changedUses = column.usesValues
                .filter(
                    use =>
                        (use.value as CalibrationUsesValModel).isChanged &&
                        !(use.value as CalibrationUsesValModel).isEmpty(notAllowedUseId)
                )
                .map(use => (use.value as CalibrationUsesValModel).currentState);
            return result.concat(changedUses);
        }, [] as ICalibrationUsesVal[]);
    }

    getDataToDelete(notAllowedUse: AllowedUseModel): number[] {
        if (!notAllowedUse) {
            return [];
        }
        const notAllowedUseId = notAllowedUse.Id;
        return this.table.reduce((result, column) => {
            const changedUses = column.usesValues
                .filter(
                    use =>
                        (use.value as CalibrationUsesValModel).isChanged &&
                        (use.value as CalibrationUsesValModel).isEmpty(notAllowedUseId)
                )
                .map(use => (use.value as CalibrationUsesValModel).currentState.Id);
            return result.concat(changedUses);
        }, [] as number[]);
    }

    getValidationResults(
        ignoreRequiredFields: boolean = false
    ): { [property: number]: ICalibrationSetValidationResult } {
        const result: { [property: number]: ICalibrationSetValidationResult } = {};
        this.table.forEach(column => {
            const values = column.usesValues;
            result[column.calibrationSet.Id] = {
                isValid: values.every(use => (use.value as CalibrationUsesValModel).isValid()),
                calibrationSet: column.calibrationSet,
                calibrationSetName: column.calibrationSet.Name,
                ignoreRequiredFields,
                validations: column.getValidationResults(ignoreRequiredFields)
            };
        });
        return result;
    }
}
